import * as z from 'zod';

/* ── Tab Primitives ───────────────────────────────────────────────────────── */

export const courierTabSchema = z.enum([
  'pickup', // Самовывоз
  'cash_delivery', // При сдаче
  'bundle', // Bundle
  'idle', // Idle 20+
  'late', // Late 5+
  'offline', // Offline
  'blocked', // Blocked
  'fired', // Fired
  'cash_limit', // Cash 500K+
]);
export type CourierTab = z.infer<typeof courierTabSchema>;

export const COURIER_TAB_LABELS: Record<CourierTab, string> = {
  pickup: 'Самовывоз',
  cash_delivery: 'При сдаче',
  bundle: 'Bundle',
  idle: 'Idle 20+',
  late: 'Late 5+',
  offline: 'Offline',
  blocked: 'Blocked',
  fired: 'Fired',
  cash_limit: 'Cash 500K+',
};

/* ── Branch Primitives ────────────────────────────────────────────────────── */

export const branchItemSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type BranchItem = z.infer<typeof branchItemSchema>;

/* ── Courier List Item DTO ────────────────────────────────────────────────── */

export const courierListItemSchema = z.object({
  id: z.string(),
  order_id: z.string(),
  name: z.string(),
  status: z.string(),
  status_label: z.string(),
  status_subtext: z.string().optional(),
  transport_time: z.string(),
  orders_count: z.number(),
  balance_uzs: z.number(),
  balance_formatted: z.string(),
  branch_id: z.string().optional(),
  branch_name: z.string().optional(),
  phone: z.string().optional(),
  is_blocked: z.boolean().default(false),
});
export type CourierListItem = z.infer<typeof courierListItemSchema>;

/* ── Pagination Meta & Response ───────────────────────────────────────────── */

export const courierPaginationMetaSchema = z.object({
  page: z.number().int().nonnegative(),
  pageSize: z.number().int().positive().default(20),
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
});
export type CourierPaginationMeta = z.infer<typeof courierPaginationMetaSchema>;

export const courierListResponseSchema = z.preprocess(
  (val: unknown) => {
    if (val && typeof val === 'object') {
      const record = val as Record<string, unknown>;
      const items = (record.items ?? record.data ?? []) as unknown[];
      const meta = (record.meta ?? {
        page: 1,
        pageSize: 20,
        total: items.length,
        totalPages: 1,
      }) as Record<string, unknown>;
      return {
        ...record,
        items,
        data: items,
        meta: {
          page: Number(meta.page ?? 1),
          pageSize: Number(meta.pageSize ?? meta.page_size ?? 20),
          total: Number(meta.total ?? items.length),
          totalPages: Number(meta.totalPages ?? meta.total_pages ?? 1),
        },
      };
    }
    return val;
  },
  z.object({
    items: z.array(courierListItemSchema),
    data: z.array(courierListItemSchema),
    meta: courierPaginationMetaSchema,
  }),
);
export type CourierListResponse = z.infer<typeof courierListResponseSchema>;

/* ── Tab Counters DTO ─────────────────────────────────────────────────────── */

export const courierCountsDtoSchema = z.object({
  pickup: z.number().int().nonnegative().default(0),
  cash_delivery: z.number().int().nonnegative().default(0),
  bundle: z.number().int().nonnegative().default(0),
  idle: z.number().int().nonnegative().default(0),
  late: z.number().int().nonnegative().default(0),
  offline: z.number().int().nonnegative().default(0),
  blocked: z.number().int().nonnegative().default(0),
  fired: z.number().int().nonnegative().default(0),
  cash_limit: z.number().int().nonnegative().default(0),
});
export type CourierCountsDto = z.infer<typeof courierCountsDtoSchema>;

/* ── Search / Query Params Schema ─────────────────────────────────────────── */

export const courierParamsSchema = z.object({
  tab: courierTabSchema.optional().default('pickup'),
  branch: z.string().optional().default('all'),
  date: z.string().optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().positive().optional().default(1),
  pageSize: z.coerce.number().int().positive().optional().default(20),
});
export type CourierParams = z.input<typeof courierParamsSchema>;
export type CourierParamsOutput = z.output<typeof courierParamsSchema>;

/* ── Mutation Payloads ────────────────────────────────────────────────────── */

export const updateCourierStatusPayloadSchema = z.object({
  status: z.string(),
  is_blocked: z.boolean().optional(),
  reason: z.string().optional(),
});
export type UpdateCourierStatusPayload = z.infer<typeof updateCourierStatusPayloadSchema>;

export const updateCourierPayloadSchema = z.object({
  name: z.string().min(1).optional(),
  phone: z.string().optional(),
  branch_id: z.string().optional(),
  branch_name: z.string().optional(),
});
export type UpdateCourierPayload = z.infer<typeof updateCourierPayloadSchema>;
